import React from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { LogOut, Languages, LayoutDashboard, UtensilsCrossed, Users, Grid3x3, Settings as SettingsIcon, Receipt, ClipboardList, Coffee, BarChart3 } from "lucide-react";
import Logo from "@/components/Logo";

const NAV_BY_ROLE = {
    owner: [
        { to: "/dashboard", icon: LayoutDashboard, key: "dashboard" },
        { to: "/dashboard/menu", icon: UtensilsCrossed, key: "menu" },
        { to: "/dashboard/tables", icon: Grid3x3, key: "tables" },
        { to: "/dashboard/orders", icon: ClipboardList, key: "orders" },
        { to: "/dashboard/reports", icon: BarChart3, key: "reports" },
        { to: "/dashboard/staff", icon: Users, key: "staff" },
        { to: "/dashboard/settings", icon: SettingsIcon, key: "settings" },
    ],
    manager: [
        { to: "/dashboard", icon: LayoutDashboard, key: "dashboard" },
        { to: "/dashboard/menu", icon: UtensilsCrossed, key: "menu" },
        { to: "/dashboard/tables", icon: Grid3x3, key: "tables" },
        { to: "/dashboard/orders", icon: ClipboardList, key: "orders" },
        { to: "/dashboard/reports", icon: BarChart3, key: "reports" },
        { to: "/dashboard/staff", icon: Users, key: "staff" },
    ],
    cashier: [
        { to: "/cashier", icon: Receipt, key: "live_orders" },
        { to: "/cashier/tables", icon: Grid3x3, key: "tables" },
    ],
    waiter: [
        { to: "/waiter", icon: Coffee, key: "new_order" },
        { to: "/waiter/orders", icon: ClipboardList, key: "orders" },
    ],
    kitchen: [
        { to: "/kitchen", icon: UtensilsCrossed, key: "orders" },
    ],
};

export default function Layout() {
    const { user, t, lang, setLang, logout, dir } = useApp();
    const location = useLocation();
    const role = user?.role;
    const nav = NAV_BY_ROLE[role] || [];

    return (
        <div className="min-h-screen flex flex-col md:flex-row coffee-bg" dir={dir}>
            {/* Sidebar */}
            <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-e border-border bg-card/40 backdrop-blur-sm flex md:flex-col flex-row items-center md:items-stretch p-3 md:p-5 md:gap-1 gap-2 overflow-x-auto md:overflow-visible">
                <div className="hidden md:block mb-6">
                    <Logo size={48} />
                </div>
                <div className="md:hidden">
                    <Logo size={36} withWordmark={false} />
                </div>
                <nav className="flex md:flex-col flex-row md:gap-1 gap-2 flex-1 md:w-full">
                    {nav.map((n) => {
                        const Icon = n.icon;
                        const active = location.pathname === n.to;
                        return (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.key}`}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm whitespace-nowrap transition-colors ${active ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-secondary"}`}
                            >
                                <Icon className="h-4 w-4" />
                                <span className="hidden md:inline">{t(n.key)}</span>
                            </Link>
                        );
                    })}
                </nav>
                <div className="hidden md:block mt-auto pt-4 border-t border-border w-full">
                    <div className="px-2 py-2 text-xs text-muted-foreground">
                        {user?.name} · <span className="capitalize">{t(role)}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="w-full justify-start gap-2" data-testid="lang-toggle">
                        <Languages className="h-4 w-4" />{t("switch_lang")}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={logout} className="w-full justify-start gap-2 text-destructive hover:text-destructive" data-testid="logout-btn">
                        <LogOut className="h-4 w-4" />{t("logout")}
                    </Button>
                </div>
                <div className="md:hidden flex gap-1 ms-auto">
                    <Button variant="ghost" size="icon" onClick={() => setLang(lang === "ar" ? "en" : "ar")} data-testid="lang-toggle-mobile">
                        <Languages className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn-mobile">
                        <LogOut className="h-4 w-4" />
                    </Button>
                </div>
            </aside>
            <main className="flex-1 p-4 md:p-8 max-w-full overflow-x-hidden">
                <Outlet />
            </main>
        </div>
    );
}
